import { useTranslation } from 'react-i18next';
import React, { useContext, useRef } from 'react';

import { useAutoRenewal } from './useAutoRenewal.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { AutoRenewalBodyWeb } from './AutoRenewalBodyWeb';
import { SubscriptionDetailsStackRoutes, SubscriptionDetailsStackScreenProps } from '../../screens/subscriptionDetails/type';
import { View } from 'dripsy';
import { UniTabHeader } from '@/design-system/components/tabs';
import { ModalHeader } from '@/design-system/components/modal';
import { EarnSubscriptionDetails, UserEarnDetailResponse } from '@utx/utx-client';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { EarnStackParamList, EarnStackRoutes } from '../..';
import BottomSheet from '@gorhom/bottom-sheet';

export interface AutoRenewalProps {
  subscription: UserEarnDetailResponse;
  onLayout: (event: any) => void;
  onRefresh?: () => void;
}

export enum Action {
  ENABLE = 'Enable',
  DISABLE = 'Disable'
}

export const AutoRenewal: React.FC<SubscriptionDetailsStackScreenProps<SubscriptionDetailsStackRoutes.AutoRenew>> = ({
  route,
  navigation
}) => {
  const { subscription, onLayout, onRefresh } = route.params;
  const earnNavigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();

  const { t } = useTranslation('earn');
  const layout = useContext(LayoutContext);

  const autoRenewal = useAutoRenewal({
    subscriptionId: subscription.id,
    autoRenewal: subscription.autoRenewable,
    onClose: () => {
      if (layout.isMobile) return;
      onRefresh && onRefresh();
    },
    onRefresh
  });

  const successTitle = subscription.autoRenew
    ? t('earn-invest::disable-auto-renewal-placed.mobile-header--disable-auto-renewal')
    : t('earn-invest::enable-auto-renewal-placed.mobile-header--enable-auto-renewal');

  if (layout.isMobile) {
    const bottomSheetRef = useRef<BottomSheet>(null);
    return (
      <AutoRenewalBodyWeb
        {...route.params}
        autoRenewal={autoRenewal}
        successTitle={successTitle}
        isSuccess={autoRenewal.isSuccess}
        isAcknowledged={autoRenewal.isAcknowledged}
        toggleAcknowledge={autoRenewal.toggleAcknowledge}
        handleConfirm={autoRenewal.handleConfirm}
        onBack={() => navigation.popToTop()}
        onSuccess={() => {
          earnNavigation.navigate(EarnStackRoutes.Main);
        }}
        bottomSheetRef={bottomSheetRef}
      />
    );
  }

  return (
    <View
      sx={{
        height: layout.isMobile ? '100%' : 'auto',
        width: layout.dimensions.width,
        maxWidth: layout.isMobile ? layout.dimensions.width : Math.min(500, layout.dimensions.width),
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={onLayout}>
      {!autoRenewal.isSuccess && <Header title={successTitle} isMobile={layout.isMobile} onClose={() => navigation.popToTop()} />}
      <AutoRenewalBodyWeb
        {...route.params}
        autoRenewal={autoRenewal}
        successTitle={successTitle}
        isSuccess={autoRenewal.isSuccess}
        isAcknowledged={autoRenewal.isAcknowledged}
        toggleAcknowledge={autoRenewal.toggleAcknowledge}
        handleConfirm={autoRenewal.handleConfirm}
        onBack={() => navigation.popToTop()}
        onSuccess={() => {
          earnNavigation.navigate(EarnStackRoutes.Main);
        }}
      />
    </View>
  );
};

const Header = ({ title, isMobile, onClose }: { title: string; isMobile: boolean; onClose: () => void }) => {
  if (isMobile) {
    return <UniTabHeader title={title} onBack={onClose} />;
  }

  return <ModalHeader title={title} closeModal={onClose} />;
};
